// The player's battle history, kept in memory so the History screen opens at once with what it showed
// last time, then updates. It is fetched early, and again whenever a battle ends.
import { api } from "./api.js";

export interface HistoryItem {
  id: string;
  arena: string;
  side: "long" | "short";
  opponent: string;
  durationSec: number;
  stake: string;
  result: "W" | "L" | "D" | "R";
  net: string;
  movePct: string | null;
  reason: string | null;
  at: number;
}

export interface PeriodStats {
  battles: number;
  wins: number;
  losses: number;
  draws: number;
  /** The period's last results, newest first, at most 10: "WWLD...". */
  form: string;
  /** The run the newest result is on, like W3; null with no battles in the period. */
  run: { result: "W" | "L" | "D"; count: number } | null;
}
export type Period = "today" | "7d" | "30d" | "all";
export type HistoryStats = Record<Period, PeriodStats>;

export interface HistoryPage {
  battles: HistoryItem[];
  next: string | null;
  stats?: HistoryStats;
}

/** The first page and its totals, from the last time they loaded. Older pages are not kept. */
let first: (HistoryPage & { stats: HistoryStats }) | null = null;
let pending: Promise<HistoryPage & { stats: HistoryStats }> | null = null;

export const cachedHistory = () => first;

/** Loads the first page again. Calls made while one is on its way share it. */
export function refreshHistory(): Promise<HistoryPage & { stats: HistoryStats }> {
  pending ??= api<HistoryPage & { stats: HistoryStats }>("/v1/history")
    .then((page) => (first = page))
    .finally(() => (pending = null));
  return pending;
}

/** Fetches ahead of time so the screen has something to show; a failure here is ignored. */
export function prefetchHistory(): void {
  refreshHistory().catch(() => {});
}

export function olderHistory(before: string): Promise<HistoryPage> {
  return api<HistoryPage>(`/v1/history?before=${encodeURIComponent(before)}`);
}
